import {
  AWTEventData,
  AWTEventProperties,
  AWTLogger,
  AWTLogManager,
  AWTPropertyStorageOverride,
  AWTSessionState
} from '@aria/webjs-sdk'

import { assertValidLogItem, assertValidCommonProperties } from './validation'
import {
  Audience,
  ILogItem,
  IPrimitiveProperties,
  IReportDataLogItem,
  IReportDataProperties,
  IUnexpectedLogItem,
  IUnexpectedProperties,
  ITraceLogItem,
  ITraceLogBufferItem,
  ITraceProperties,
  REPORT_DATA,
  SamplePolicy,
  UNEXPECTED,
  TRACE,
  ISignedInDataProperties,
  IPiiLogItem
} from './types'
import { RingBuffer } from './lib/RingBuffer'

const SCHEMA_VERSION = '1.2'
const NORMAL_PRIORITY = 2
export const USERTYPE_ORGIDUSEROBJECTID = 'UserObjectId'
export const USERTYPE_MSACID = 'MSACID'

export interface IUserContext {
  /**
   * UserId that will be set in the aria context
   */
  userId: string
  /**
   * UserIdType for the provided UserId
   */
  userType: string
  /**
   * For OrgId users, the sharepoint tenantid
   */
  tenantId?: string
}

export interface IAriaLoggerOptions {
  /**
   * tenantID is the tenant id all events should be piped to for this instance of AriaLogger
   */
  tenantID: string
  /**
   * The identifier for the application being logged (e.g. application Name)
   */
  appID: string
  /**
   * The version of the application being logged
   */
  appVersion: string
  /**
   * User audience (deployment rings). Valid values are:
   *  Automation, Dogfood, Microsoft, Insiders, and Production
   */
  audience: Audience
  /**
   * Only pass a device id if you wish to set the device id for Aria manually
   * The sticky notes sdk instance of AriaLogger passes in its internal device id
   */
  deviceID?: string
  /**
   * Due to the singleton nature of the Aria SDK, initialize must only be called once.
   * However an Arialogger created via AWTLogManager.getLogger() will only start flushing data
   * after AWTLogManager.initialize() has been called at least once.
   *
   * If you are creating this instance before creating any other instances of AriaLogger,
   * you should pass shouldInitialize = true.
   *
   * If you have already setup an instance of AriaLogger before initializing this instance of AriaLogger,
   * pass shouldInitialize = false
   */
  shouldInitialize?: boolean
  /**
   * https://www.aria.ms/developers/how-to/ua-getting-started/
   * Enable user analytics with aria
   */
  enableAutoUserSession?: boolean
  /**
   * Supplying propertyStorageOverride will additionally explicitly (via options) disable Aria cookie usage
   */
  propertyStorageOverride?: AWTPropertyStorageOverride
  /**
   * Transmit profile. Use REAL_TIME or NEAR_REAL_TIME or BEST_EFFORT
   */
  transmitProfile: 'REAL_TIME' | 'NEAR_REAL_TIME' | 'BEST_EFFORT'
}

export interface IValidAriaLoggerOptions extends IAriaLoggerOptions {
  audience: Audience
  shouldInitialize: boolean
  isFlushingDisabled: boolean
  transmitProfile: 'REAL_TIME' | 'NEAR_REAL_TIME' | 'BEST_EFFORT'
  enableAutoUserSession: boolean
  disableCookiesUsage: boolean
  propertyStorageOverride?: AWTPropertyStorageOverride
}

export const defaultAriaLoggerOptions = {
  audience: Audience.Dogfood,
  shouldInitialize: true,
  isFlushingDisabled: false,
  // We use realtime as default
  // https://www.aria.ms/developers/how-to/set-event-priority/
  transmitProfile: 'REAL_TIME',
  enableAutoUserSession: false,
  disableCookiesUsage: false,
  propertyStorageOverride: undefined,
}

const NO_VALUE = ''

export class AriaLogger {
  private static loggers: { [tenantID: string]: AriaLogger } = {}
  private logger: AWTLogger
  private audience: Audience
  private userContext: IUserContext
  readonly traceBuffer: RingBuffer<ITraceLogBufferItem>

  constructor (options: IAriaLoggerOptions) {
    const optionsWithDefaults: IValidAriaLoggerOptions = {
      ...defaultAriaLoggerOptions,
      ...options,
    }
    const { tenantID, audience, shouldInitialize, isFlushingDisabled, enableAutoUserSession } = optionsWithDefaults
    const { propertyStorageOverride } = optionsWithDefaults

    if (AriaLogger.loggers[tenantID]) {
      return AriaLogger.loggers[tenantID]
    } else if (AriaLogger.hasAria()) {
      if (AWTLogManager && AWTLogManager.setTransmitProfile) {
        AWTLogManager.setTransmitProfile(optionsWithDefaults.transmitProfile)
      } else {
        console.warn(
          '[AriaLogger] AWTLogManager.setTransmitProfile was not found. Include aria-webjs-sdk in HEAD or expose AWTLogManager on window.'
        )
      }

      this.audience = audience
      this.setContext(optionsWithDefaults)
      this.logger = shouldInitialize
        ? AWTLogManager.initialize(tenantID, {
          enableAutoUserSession,
          disableCookiesUsage: !!propertyStorageOverride,
          propertyStorageOverride,
        })
        : AWTLogManager.getLogger(tenantID)
      this.traceBuffer = new RingBuffer<ITraceLogBufferItem>(100)

      if (shouldInitialize && !isFlushingDisabled) {
        if (window && window.addEventListener) {
          window.addEventListener('beforeunload', (_: BeforeUnloadEvent) => {
            // Make sure we flush all aria events before closing the window
            AWTLogManager.flushAndTeardown()
          })
        } else {
          console.warn(
            '[AriaLogger] Could not register the beforeunload callback for flushing logs.'
          )
        }
      }
    } else {
      console.warn(
        '[AriaLogger] AWTLogManager not found. Include aria-webjs-sdk in HEAD or expose AWTLogManager on window to enable Aria logging.'
      )
    }
  }

  public setUserContext(userContext: IUserContext) {
    this.userContext = userContext
  }

  private setContext (options: IValidAriaLoggerOptions) {
    const {
      appID,
      appVersion,
      deviceID,
    } = options

    const ariaContext = AWTLogManager.getSemanticContext()
    if (deviceID) {
      ariaContext.setDeviceId(deviceID)
    }
    ariaContext.setAppId(appID)
    ariaContext.setAppVersion(appVersion)
  }

  private hasLogger (): boolean {
    return this.logger !== undefined
  }

  private buildCommonProperties (item: ILogItem): IReportDataProperties | ISignedInDataProperties {
    const namespace = this.buildNamespace(item)
    const properties: IReportDataProperties | ISignedInDataProperties = {
      Audience: this.audience,
      SchemaVersion: SCHEMA_VERSION,
      ShortEventName: item.ShortEventName,
      Namespace: namespace,
      EventName: `${namespace}.${item.ShortEventName}`,
      SamplePolicy: SamplePolicy.Critical,
      ExpirationDate: item.ExpirationDate,
      SessionCorrelationId: item.SessionCorrelationId ? item.SessionCorrelationId : NO_VALUE,
      IsExportable: item.IsExportable ? '1' : '0',
      // HostApp is outside of the Common Schema, but is added to allow multiple host contexts for a single Aria tenant
      HostApp: item.HostApp ? item.HostApp : NO_VALUE,
    }

    // Note we don't use the aria context setUserId method since it:
    // 1. Sets the idType as an enum instead of a string
    // 2. Provides no way of setting the OMSTenantId
    if (this.userContext) {
      properties['UserInfo.Id'] = this.userContext.userId
      properties['UserInfo.IdType'] = this.userContext.userType
      if (this.userContext.tenantId) {
        properties['UserInfo.OMSTenantId'] = this.userContext.tenantId
      }
    }

    assertValidCommonProperties(properties)
    return properties
  }

  private buildReportDataProperties (item: IReportDataLogItem): IReportDataProperties {
    const properties: IReportDataProperties = {
      ...this.buildCommonProperties(item),
    }
    return AriaLogger.addContextToProperties<IReportDataProperties>(
      properties,
      item.Context
    )
  }

  private buildEventPropertiesWithPii(piiLogItem: IPiiLogItem): AWTEventProperties {
    const piiItems = piiLogItem.PiiItems
    const eventProperties = new AWTEventProperties()
    let properties: IReportDataProperties = {
      ...this.buildCommonProperties(piiLogItem),
    }
    properties = AriaLogger.addContextToProperties<IReportDataProperties>(properties, piiLogItem.Context)
    // The Aria SDK requires that PII data is tagged via the AWTEventProperties interface. Add common properties
    // to the eventProperties object we are building
    Object.keys(properties).forEach(property => {
      eventProperties.setProperty(property, properties[property])
    })
    if (Array.isArray(piiItems)) {
      // Add Pii properties to the eventProperties object
      piiItems.forEach(property => {
        eventProperties.setPropertyWithPii(property.Name, property.Value, property.Pii, property.Type)
      })
    }
    return eventProperties
  }

  private buildUnexpectedProperties (item: IUnexpectedLogItem): IUnexpectedProperties {
    const properties = {
      ...this.buildCommonProperties(item),
      ErrorCode1: item.ErrorCode1 ? `${ item.ErrorCode1 }` : NO_VALUE,
      ErrorCode2: item.ErrorCode2 ? `${ item.ErrorCode2 }` : NO_VALUE,
      ErrorTag: item.ErrorTag ? `${ item.ErrorTag }` : NO_VALUE,
      ErrorType: item.ErrorType ? `${ item.ErrorType }` : NO_VALUE,
    }

    return AriaLogger.addContextToProperties<IUnexpectedProperties>(
      properties,
      item.Context
    )
  }

  private buildTraceProperties (item: ITraceLogBufferItem, unexpectedProperties: IUnexpectedProperties): ITraceProperties {
    const properties = {
      ...this.buildCommonProperties(item),
      ErrorTag: item.ErrorTag ? item.ErrorTag : NO_VALUE,
      TraceTime: item.TraceTime,
      TriggerError: unexpectedProperties.EventName,
      TriggerErrorCode: unexpectedProperties.ErrorCode1,
      TriggerErrorType: unexpectedProperties.ErrorType,
    }

    return AriaLogger.addContextToProperties<ITraceProperties>(
      properties,
      item.Context
    )
  }

  protected buildNamespace (item: ILogItem): string {
    return item.Namespace
  }

  private withLogger (fn: () => void) {
    if (this.hasLogger()) { fn() }
  }

  /**
   * LogReportData is best used to collect
   * an isolated data point that is intended to be used in a report
   * or otherwise closely monitored to analyze how the product is being used
   */
  logReportData (item: IReportDataLogItem) {
    assertValidLogItem(item)
    this.withLogger(() => {
      const properties = this.buildReportDataProperties(item)
      const eventData: AWTEventData = {
        name: REPORT_DATA,
        properties,
        priority: NORMAL_PRIORITY,
      }
      this.logger.logEvent(eventData)
    })
  }

  /**
   * LogReportDataWithPii is to be used when logging a property that is considered Pii
   * (sensitive user data), such as a user PUID, that will be scrubbed in logs
   */
  logReportDataWithPii(item: IPiiLogItem) {
    assertValidLogItem(item)
    this.withLogger(() => {
      const properties = this.buildEventPropertiesWithPii(item)
      properties.setName(REPORT_DATA)
      properties.setEventPriority(NORMAL_PRIORITY)
      this.logger.logEvent(properties)
    })
  }

  /**
   * logUnexpected is only to be used for sending an unexpected, critical error
   * Unexpected logs need to be treated as product bug to process forward
   * logUnexpected doesn’t provide the ability for the instrumenter to add custom properties or additional context
   * calling logUnexpected will automatically upload a ring buffer of traces
   */
  logUnexpected (item: IUnexpectedLogItem) {
    assertValidLogItem(item)
    this.withLogger(() => {
      const properties = this.buildUnexpectedProperties(item)
      this.flushTraceBuffer(properties)
      const eventData: AWTEventData = {
        name: UNEXPECTED,
        properties,
        priority: NORMAL_PRIORITY,
      }
      this.logger.logEvent(eventData)
    })
  }

  /**
   * logTrace is best used for logging a diagnostic trace
   * traces are not immediately sent as events, they are put into a ring buffer
   * they are flushed when logUnexpected is called
   */
  logTrace (item: ITraceLogItem) {
    assertValidLogItem(item)
    this.withLogger(() => {
      this.traceBuffer.add({
        ...item,
        TraceTime: (new Date()).toISOString(),
      })
    })
  }

  /**
   * https://www.aria.ms/developers/how-to/ua-web/
   * logSession is used to log the beginning of a session and the end of a session in a webapp
   * in general, we can use enableAutoUserSession, however in cases where the window events aren't supported (e.g. Microsoft Teams)
   * we have to call these methods manually, so this library exposes them
   */
  logSession(sessionState: AWTSessionState) {
    this.withLogger(() => {
      this.logger.logSession(sessionState)
    })
  }

  getSessionId() {
    return this.logger.getSessionId()
  }

  private flushTraceBuffer (triggerProperties: IUnexpectedProperties) {
    const traces = this.traceBuffer.toArray()
    traces.forEach(trace => {
      const properties = this.buildTraceProperties(trace, triggerProperties)
      const eventData: AWTEventData = {
        name: TRACE,
        properties,
        priority: NORMAL_PRIORITY,
      }
      this.logger.logEvent(eventData)
    })
    this.traceBuffer.clear()
  }

  static hasAria () {
    // tslint:disable-next-line:no-any
    return AWTLogManager !== undefined
  }

  private static addContextToProperties<T extends IPrimitiveProperties> (properties: T, context?: IPrimitiveProperties): T {
    if (!context) { return properties }
    const withContext: T = { ...properties as Object } as T
    Object.keys(context).forEach(key => {
      if (!(key in properties)) {
        withContext[key] = context[key]
      } else {
        throw new Error(`Cannot add context ${key} to properties. ${key} already exists in properties.`)
      }
    })
    return withContext
  }
}